import { BoxProps } from '@everlywell/ui-kit';
import React, { useEffect, useRef } from 'react';
import { logError } from 'utils/helpers';

export type VideoComponentProps =
  React.VideoHTMLAttributes<HTMLVideoElement> & {
    url: string;
    contentType: string;
    objectFit?: BoxProps['objectFit'];
    objectPosition?: BoxProps['objectPosition'];
  };

// Default props for the video element
const defaultVideoProps = {
  objectFit: 'cover' as const,
  objectPosition: 'center' as const,
  autoPlay: true,
  muted: true,
  controls: false,
  loop: false,
  height: 'auto',
  width: '100%',
  playsInline: true,
};

export default function VideoComponent({
  url,
  contentType,
  ...videoProps
}: VideoComponentProps) {
  const mergedProps = { ...defaultVideoProps, ...videoProps };
  const { objectFit, objectPosition, ...htmlVideoProps } = mergedProps;
  const videoRef = useRef<HTMLVideoElement>(null);

  // Use useEffect to handle video playback when component mounts
  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement && htmlVideoProps.autoPlay) {
      // For iOS Safari, we need to manually call play()
      const playPromise = videoElement.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          logError('Error on auto playing video', {
            component: 'VideoComponent',
            error,
          });
        });
      }
    }
  }, [htmlVideoProps.autoPlay, url]);

  return (
    <video
      ref={videoRef}
      style={{
        objectFit: objectFit as any,
        objectPosition: objectPosition as any,
      }}
      {...htmlVideoProps}
      /**
       * Force boolean attributes to be present in the DOM
       * This is crucial for iOS Safari autoplay which requires both muted and playsInline
       *
       * In React, when you pass a boolean prop like muted={true},
       * React doesn't always render it as an HTML attribute in the DOM.
       * For HTML boolean attributes to work properly,
       * they need to be present in the DOM regardless of their value.
       */
      muted={htmlVideoProps.muted || undefined}
      playsInline={htmlVideoProps.playsInline || undefined}
      autoPlay={htmlVideoProps.autoPlay || undefined}
      controls={htmlVideoProps.controls || undefined}
      loop={htmlVideoProps.loop || undefined}
      poster={htmlVideoProps.poster || undefined}
    >
      <source src={url} type={contentType} />
      Your browser does not support the video tag.
    </video>
  );
}
